import CLIMATE_DEMO_SIGNALS from './climateMonitoringDemoSignals';

import type IMonitoringRun from './interfaces/IMonitoringRun';
import type IMonitoringSchedule from './interfaces/IMonitoringSchedule';
import type {
  IFinanceMonitoringSignalResult,
  IOwnershipMonitoringSignalResult,
  IRegulationMonitoringSignalResult,
  IReputationMonitoringSignalResult,
  TMonitoringAnySignalResult,
} from './interfaces/IMonitoringSignalResult';

/**
 * Demo fixtures aligned with:
 * - docs/mockups/ai-agents/reputation-monitoring/data/ (media-sentiment / articles_found)
 * - docs/mockups/ai-agents/regulation-monitoring/data/demo-data.json (regulation-tracking)
 * - docs/mockups/ai-agents/finance-monitoring/data/demo-data.embed.js (financial-risk)
 * - docs/mockups/ai-agents/ownership-monitoring/data/demo-data.embed.js (ownership contagion)
 * - docs/mockups/ai-agents/climate-monitoring/data/demo-data.embed.js (climate-risk)
 *
 * Schedule entity names (e.g. Test 1, Test 2) are resolved to live board unit IDs at runtime
 * via resolveMonitoringEntitiesFromBoard when opening schedule run results in mock mode.
 */
export const MONITORING_AGENT_DEMO_SCHEDULES: IMonitoringSchedule[] = [
  {
    id: 'sched-001',
    title: 'Tier-1 suppliers — weekly sentiment',
    sessionType: 'reputation-monitoring',
    subType: 'media-sentiment',
    unitIds: [0, 0],
    entities: [
      { unitId: 0, name: 'Test 1' },
      { unitId: 0, name: 'Test 2' },
    ],
    schedule: {
      mode: 'recurring',
      timezone: 'America/New_York',
      recurrence: {
        frequency: 'weekly',
        interval: 1,
        daysOfWeek: ['MO', 'TH'],
        timeOfDay: '08:00:00',
        startDate: '2026-07-21',
        endDate: '2026-12-31',
      },
    },
    nextRunAt: '2026-07-17T12:00:00.000Z',
    lastRunAt: '2026-07-15T12:00:00.000Z',
    lastRunStatus: 'completed',
    paused: false,
  },
  {
    id: 'sched-002',
    title: 'Attaima Internal — daily scan',
    sessionType: 'reputation-monitoring',
    subType: 'media-sentiment',
    unitIds: [0],
    entities: [
      { unitId: 0, name: 'Attaima Internal Manufacturing' },
    ],
    schedule: {
      mode: 'recurring',
      timezone: 'Europe/Paris',
      recurrence: {
        frequency: 'daily',
        interval: 1,
        daysOfWeek: [],
        timeOfDay: '06:00:00',
        startDate: '2026-07-01',
        endDate: null,
      },
    },
    nextRunAt: '2026-07-16T04:00:00.000Z',
    lastRunAt: '2026-07-15T04:00:00.000Z',
    lastRunStatus: 'running',
    paused: false,
  },
  {
    id: 'sched-reg-001',
    title: 'Food-contact materials — weekly regulation scan',
    sessionType: 'regulation-monitoring',
    subType: 'regulation-tracking',
    unitIds: [47770, 47771, 47431],
    entities: [
      { unitId: 47770, name: 'Polyethylene Film' },
      { unitId: 47771, name: 'Adhesive Materials' },
      { unitId: 47431, name: 'Polyurethane Probe Cover Set' },
    ],
    schedule: {
      mode: 'recurring',
      timezone: 'Europe/Dublin',
      recurrence: {
        frequency: 'weekly',
        interval: 1,
        daysOfWeek: ['MO', 'TH'],
        timeOfDay: '07:00:00',
        startDate: '2026-07-21',
        endDate: '2026-12-31',
      },
    },
    nextRunAt: '2026-07-21T06:00:00.000Z',
    lastRunAt: '2026-07-14T06:00:00.000Z',
    lastRunStatus: 'completed',
    paused: false,
  },
  {
    id: 'sched-fin-001',
    title: 'Key suppliers — weekly financial scan',
    sessionType: 'finance-monitoring',
    subType: 'financial-risk',
    unitIds: [47426, 47427, 47428],
    entities: [
      { unitId: 47426, name: 'Polymer Technologies' },
      { unitId: 47427, name: 'Huntsman Corporation' },
      { unitId: 47428, name: 'Wacker Chemie AG' },
    ],
    schedule: {
      mode: 'recurring',
      timezone: 'America/New_York',
      recurrence: {
        frequency: 'weekly',
        interval: 1,
        daysOfWeek: ['MO', 'TH'],
        timeOfDay: '06:00:00',
        startDate: '2026-07-01',
        endDate: null,
      },
    },
    nextRunAt: '2026-07-21T10:00:00.000Z',
    lastRunAt: '2026-07-17T10:00:00.000Z',
    lastRunStatus: 'completed',
    paused: false,
  },
  {
    id: 'sched-own-001',
    title: 'Key suppliers — weekly ownership scan',
    sessionType: 'ownership-monitoring',
    subType: 'ownership-monitoring',
    unitIds: [47426, 47427, 47428],
    entities: [
      { unitId: 47426, name: 'Polymer Technologies' },
      { unitId: 47427, name: 'Huntsman Corporation' },
      { unitId: 47428, name: 'Wacker Chemie AG' },
    ],
    schedule: {
      mode: 'recurring',
      timezone: 'America/New_York',
      recurrence: {
        frequency: 'weekly',
        interval: 1,
        daysOfWeek: ['MO'],
        timeOfDay: '06:00:00',
        startDate: '2026-07-07',
        endDate: null,
      },
    },
    nextRunAt: '2026-07-28T10:00:00.000Z',
    lastRunAt: '2026-07-21T10:00:00.000Z',
    lastRunStatus: 'completed',
    paused: false,
  },
  {
    id: 'sched-cli-001',
    title: 'Key sites — weekly climate scan',
    sessionType: 'climate-monitoring',
    subType: 'climate-risk',
    unitIds: [47426, 51002, 52010, 48001],
    entities: [
      { unitId: 47426, name: 'Polymer Technologies' },
      { unitId: 51002, name: 'Attaima Internal Manufacturing — Dublin' },
      { unitId: 52010, name: 'Cork Filling Line Asset' },
      { unitId: 48001, name: 'AgriFeed Co-op' },
    ],
    schedule: {
      mode: 'recurring',
      timezone: 'Europe/Dublin',
      recurrence: {
        frequency: 'weekly',
        interval: 1,
        daysOfWeek: ['MO'],
        timeOfDay: '06:00:00',
        startDate: '2026-07-07',
        endDate: null,
      },
    },
    nextRunAt: '2026-07-28T05:00:00.000Z',
    lastRunAt: '2026-07-21T05:00:00.000Z',
    lastRunStatus: 'completed',
    paused: false,
  },
];

export const MONITORING_AGENT_DEMO_SCHEDULE_RUNS: Record<string, IMonitoringRun[]> = {
  'sched-001': [
    {
      id: 'run-001-03',
      scheduleId: 'sched-001',
      scheduledAt: '2026-07-15T12:00:00.000Z',
      startedAt: '2026-07-15T12:00:15.000Z',
      status: 'completed',
      entityCount: 2,
      alertCount: 3,
    },
    {
      id: 'run-001-02',
      scheduleId: 'sched-001',
      scheduledAt: '2026-07-10T12:00:00.000Z',
      startedAt: '2026-07-10T12:00:08.000Z',
      status: 'completed',
      entityCount: 2,
      alertCount: 1,
    },
    {
      id: 'run-001-01',
      scheduleId: 'sched-001',
      scheduledAt: '2026-07-07T12:00:00.000Z',
      startedAt: '2026-07-07T12:00:12.000Z',
      status: 'failed',
      entityCount: 2,
      alertCount: 0,
    },
  ],
  'sched-002': [
    {
      id: 'run-002-02',
      scheduleId: 'sched-002',
      scheduledAt: '2026-07-15T04:00:00.000Z',
      startedAt: '2026-07-15T04:00:05.000Z',
      status: 'running',
      entityCount: 1,
      alertCount: 0,
    },
    {
      id: 'run-002-01',
      scheduleId: 'sched-002',
      scheduledAt: '2026-07-14T04:00:00.000Z',
      startedAt: '2026-07-14T04:00:03.000Z',
      status: 'completed',
      entityCount: 1,
      alertCount: 2,
    },
  ],
  'sched-reg-001': [
    {
      id: 'run-reg-001-02',
      scheduleId: 'sched-reg-001',
      scheduledAt: '2026-07-14T06:00:00.000Z',
      startedAt: '2026-07-14T06:00:08.000Z',
      status: 'completed',
      entityCount: 3,
      alertCount: 2,
    },
    {
      id: 'run-reg-001-01',
      scheduleId: 'sched-reg-001',
      scheduledAt: '2026-07-07T06:00:00.000Z',
      startedAt: '2026-07-07T06:00:05.000Z',
      status: 'completed',
      entityCount: 3,
      alertCount: 1,
    },
  ],
  'sched-fin-001': [
    {
      id: 'run-fin-001-01',
      scheduleId: 'sched-fin-001',
      scheduledAt: '2026-07-17T10:00:00.000Z',
      startedAt: '2026-07-17T10:00:04.000Z',
      status: 'completed',
      entityCount: 3,
      alertCount: 4,
    },
    {
      id: 'run-fin-001-02',
      scheduleId: 'sched-fin-001',
      scheduledAt: '2026-07-14T10:00:00.000Z',
      startedAt: '2026-07-14T10:00:03.000Z',
      status: 'completed',
      entityCount: 3,
      alertCount: 3,
    },
  ],
  'sched-own-001': [
    {
      id: 'run-own-001-01',
      scheduleId: 'sched-own-001',
      scheduledAt: '2026-07-21T10:00:00.000Z',
      startedAt: '2026-07-21T10:00:06.000Z',
      status: 'completed',
      entityCount: 3,
      alertCount: 5,
    },
    {
      id: 'run-own-001-02',
      scheduleId: 'sched-own-001',
      scheduledAt: '2026-07-14T10:00:00.000Z',
      startedAt: '2026-07-14T10:00:04.000Z',
      status: 'completed',
      entityCount: 3,
      alertCount: 2,
    },
  ],
  'sched-cli-001': [
    {
      id: 'run-cli-001-01',
      scheduleId: 'sched-cli-001',
      scheduledAt: '2026-07-14T05:00:00.000Z',
      startedAt: '2026-07-14T05:00:00.000Z',
      status: 'completed',
      entityCount: 4,
      alertCount: 1,
    },
    {
      id: 'run-cli-001-02',
      scheduleId: 'sched-cli-001',
      scheduledAt: '2026-07-21T05:00:00.000Z',
      startedAt: '2026-07-21T05:00:00.000Z',
      status: 'completed',
      entityCount: 4,
      alertCount: 5,
    },
  ],
};

const REPUTATION_DEMO_SIGNALS: Record<string, IReputationMonitoringSignalResult> = {
  201: {
    articles_found: [
      {
        title: 'Insolvency proceedings reported for Polymer Technologies',
        date: '2026-07-14',
        source: 'Industry weekly',
        url: 'https://example.com/articles/polymer-insolvency',
        summary: 'Trade press reports that Polymer Technologies may enter insolvency proceedings '
          + 'after missing bond payments.',
        keywords_found: ['polymer', 'insolvency', 'supplier', 'chemicals'],
        sentiment: 'negative',
        risk_severity: 'critical',
        confidence: 0.93,
        excerpt: 'Bondholders are reportedly convening as liquidity pressures mount across the '
          + 'specialty chemicals segment.',
      },
      {
        title: 'EU chemical safety review mentions Polymer Technologies',
        date: '2026-07-12',
        source: 'ECHA bulletin',
        url: 'https://example.com/articles/polymer-reach',
        summary: 'A REACH compliance bulletin references Polymer Technologies in a broader supplier audit.',
        keywords_found: ['polymer', 'reach', 'regulatory', 'compliance'],
        sentiment: 'negative',
        risk_severity: 'medium',
        confidence: 0.81,
        excerpt: 'The bulletin cites upstream manufacturers named in an EU chemical safety review.',
      },
      {
        title: 'Labour practices discussed on social media — Huntsman Corporation',
        date: '2026-07-11',
        source: 'LinkedIn',
        url: 'https://example.com/articles/huntsman-labour',
        summary: 'Employee advocacy posts discuss working-hour policies at Huntsman Corporation contract sites.',
        keywords_found: ['huntsman', 'labour', 'ethics', 'social'],
        sentiment: 'neutral',
        risk_severity: 'low',
        confidence: 0.64,
        excerpt: 'Several posts reference overtime policies without alleging regulatory breaches.',
      },
    ],
    summary: {
      total_articles: 3,
      high_risk_count: 1,
      keywords_frequency: {
        polymer: 2,
        insolvency: 1,
        supplier: 1,
        chemicals: 1,
        reach: 1,
        regulatory: 1,
        compliance: 1,
        huntsman: 1,
        labour: 1,
        ethics: 1,
        social: 1,
      },
    },
  },
  202: {
    articles_found: [
      {
        title: 'Credit rating downgrade rumoured for Attaima Internal Manufacturing',
        date: '2026-07-15',
        source: 'Reuters',
        url: 'https://example.com/articles/attaima-credit-rating',
        summary: 'Analyst commentary suggests a possible credit rating downgrade for Attaima Internal Manufacturing.',
        keywords_found: ['attaima', 'credit', 'financial', 'rating'],
        sentiment: 'negative',
        risk_severity: 'high',
        confidence: 0.88,
        excerpt: 'Markets are pricing in weaker liquidity after supplier payment delays were discussed online.',
      },
      {
        title: 'Executive linked to procurement investigation',
        date: '2026-07-13',
        source: 'Trade press',
        url: 'https://example.com/articles/attaima-investigation',
        summary: 'Trade press links a former executive to a procurement investigation involving '
          + 'Attaima Internal Manufacturing.',
        keywords_found: ['attaima', 'investigation', 'scandal', 'procurement'],
        sentiment: 'negative',
        risk_severity: 'high',
        confidence: 0.86,
        excerpt: 'The article cites unnamed sources close to the investigation but provides no formal charges.',
      },
    ],
    summary: {
      total_articles: 2,
      high_risk_count: 2,
      keywords_frequency: {
        attaima: 2,
        credit: 1,
        financial: 1,
        rating: 1,
        investigation: 1,
        scandal: 1,
        procurement: 1,
      },
    },
  },
  'run-001-03': {
    articles_found: [
      {
        title: 'Bondholders meeting coverage intensifies for Test 1',
        date: '2026-07-15',
        source: 'Financial Times',
        url: 'https://example.com/articles/test1-bondholders',
        summary: 'Coverage of a bondholders meeting adds pressure on Test 1.',
        keywords_found: ['test 1', 'bondholders', 'financial'],
        sentiment: 'negative',
        risk_severity: 'critical',
        confidence: 0.94,
        excerpt: 'Investors are seeking clarity on refinancing options before quarter end.',
      },
      {
        title: 'REACH compliance audit referenced in industry weekly',
        date: '2026-07-14',
        source: 'Industry weekly',
        summary: 'Industry weekly references a REACH compliance audit touching Test 1.',
        keywords_found: ['test 1', 'reach', 'audit'],
        sentiment: 'negative',
        risk_severity: 'medium',
        confidence: 0.79,
        excerpt: 'The audit is part of a wider EU supplier review programme.',
      },
      {
        title: 'Union statement on working hours at Test 2',
        date: '2026-07-13',
        source: 'Regional TV',
        summary: 'A union statement on working hours receives regional TV coverage for Test 2.',
        keywords_found: ['test 2', 'union', 'labour'],
        sentiment: 'neutral',
        risk_severity: 'low',
        confidence: 0.67,
        excerpt: 'The union asked for a formal review of overtime scheduling policies.',
      },
    ],
    summary: {
      total_articles: 3,
      high_risk_count: 1,
      keywords_frequency: {
        'test 1': 2,
        bondholders: 1,
        financial: 1,
        reach: 1,
        audit: 1,
        'test 2': 1,
        union: 1,
        labour: 1,
      },
    },
  },
  'run-001-02': {
    articles_found: [
      {
        title: 'Test 1 linked to financial distress coverage',
        date: '2026-07-10',
        source: 'Industry weekly',
        url: 'https://example.com/articles/test1-distress',
        summary: 'Trade press reports heightened financial distress signals for Test 1.',
        keywords_found: ['test 1', 'supplier', 'reputation', 'high'],
        sentiment: 'negative',
        risk_severity: 'high',
        confidence: 0.91,
        excerpt: 'Creditors are reportedly convening as liquidity pressures mount.',
      },
      {
        title: 'Public discussion references Test 2',
        date: '2026-07-09',
        source: 'LinkedIn',
        summary: 'Social channels discuss labour practices linked to Test 2.',
        keywords_found: ['test 2', 'supplier', 'reputation', 'medium'],
        sentiment: 'neutral',
        risk_severity: 'medium',
        confidence: 0.62,
        excerpt: 'Employee advocacy posts mention working-hour policies.',
      },
    ],
    summary: {
      total_articles: 2,
      high_risk_count: 1,
      keywords_frequency: {
        'test 1': 1,
        'test 2': 1,
        supplier: 2,
        reputation: 2,
        high: 1,
        medium: 1,
      },
    },
  },
  'run-002-01': {
    articles_found: [
      {
        title: 'Supplier payment delays discussed online',
        date: '2026-07-14',
        source: 'Twitter/X',
        summary: 'Social posts discuss supplier payment delays involving Attaima Internal Manufacturing.',
        keywords_found: ['attaima', 'payments', 'supplier'],
        sentiment: 'negative',
        risk_severity: 'medium',
        confidence: 0.72,
        excerpt: 'Several posts reference delayed invoices without citing official filings.',
      },
      {
        title: 'Former CFO interview raises questions',
        date: '2026-07-13',
        source: 'Business news',
        url: 'https://example.com/articles/attaima-cfo',
        summary: 'A former CFO interview raises questions about procurement governance at '
          + 'Attaima Internal Manufacturing.',
        keywords_found: ['attaima', 'cfo', 'governance', 'scandal'],
        sentiment: 'negative',
        risk_severity: 'high',
        confidence: 0.9,
        excerpt: 'The interview references internal controls without confirming an active investigation.',
      },
    ],
    summary: {
      total_articles: 2,
      high_risk_count: 1,
      keywords_frequency: {
        attaima: 2,
        payments: 1,
        supplier: 1,
        cfo: 1,
        governance: 1,
        scandal: 1,
      },
    },
  },
};

const REGULATION_DEMO_SIGNALS: Record<string, IRegulationMonitoringSignalResult> = {
  401: {
    regulations_found: [
      {
        id: 'reg-eu-fcm-001',
        product_name: 'Polyethylene Film',
        product_unit_id: 47770,
        related_materials: ['Adhesive Materials', 'Non-Woven Polypropylene Fabric'],
        factory_location: {
          label: 'ATTAIMAIRELAND / Dublin, Ireland',
          country: 'Ireland',
          region: 'Leinster',
        },
        regulation_name: 'Framework Regulation (EC) No. 1935/2004',
        regulatory_body: 'European Commission',
        jurisdiction: 'European Union',
        source_type: 'official',
        source_links: [
          {
            label: 'European Commission - Food Contact Materials',
            url: 'https://food.ec.europa.eu/food-safety/chemical-safety/food-contact-materials_en',
          },
          {
            label: 'EFSA - Food Contact Materials',
            url: 'https://www.efsa.europa.eu/en/topics/topic/food-contact-materials',
          },
        ],
        impact_level: 'medium',
        impact_summary:
          'If this film is sold into food-contact packaging or process-contact applications, '
          + 'compliance evidence is required under the EU food-contact framework.',
        approval_state: 'in-force',
        enacted_date: '2004-10-27',
        effective_date: '2005-12-03',
        expected_enforcement_date: null,
        confidence: 0.86,
        recommended_action:
          'Confirm end use and collect declaration-of-compliance evidence for EU customers.',
      },
      {
        id: 'reg-eu-bpa-002',
        product_name: 'Adhesive Materials',
        product_unit_id: 47771,
        related_materials: ['Polyethylene Film', 'Polyurethane Film'],
        factory_location: {
          label: 'ATTAIMAIRELAND / Dublin, Ireland',
          country: 'Ireland',
          region: 'Leinster',
        },
        regulation_name:
          'Commission Regulation (EU) 2024/3190 on BPA and other bisphenols in certain '
          + 'food contact materials',
        regulatory_body: 'European Commission',
        jurisdiction: 'European Union',
        source_type: 'official',
        source_links: [
          {
            label: 'EUR-Lex - Regulation (EU) 2024/3190',
            url: 'https://eur-lex.europa.eu/eli/reg/2024/3190/',
          },
        ],
        impact_level: 'high',
        impact_summary:
          'Food-contact adhesives containing restricted bisphenols may require reformulation '
          + 'or market withdrawal after transition deadlines.',
        approval_state: 'approved-not-effective',
        enacted_date: '2024-12-19',
        effective_date: '2025-01-20',
        expected_enforcement_date: '2026-07-20',
        confidence: 0.81,
        recommended_action:
          'Check whether any adhesive chemistry sold into food-contact uses contains BPA or '
          + 'related bisphenols and map transition exposure by customer.',
      },
    ],
    summary: {
      total_results: 2,
      high_impact_count: 1,
      critical_impact_count: 0,
    },
  },
  'run-reg-001-02': {
    regulations_found: [
      {
        id: 'reg-eu-fcm-010',
        product_name: 'Polyurethane Probe Cover Set',
        product_unit_id: 47431,
        related_materials: ['Polyurethane Film', 'Silicone Gel'],
        factory_location: {
          label: 'ATTAIMAIRELAND / Dublin, Ireland',
          country: 'Ireland',
          region: 'Leinster',
        },
        regulation_name: 'Framework Regulation (EC) No. 1935/2004',
        regulatory_body: 'European Commission',
        jurisdiction: 'European Union',
        source_type: 'official',
        source_links: [
          {
            label: 'European Commission - Food Contact Materials',
            url: 'https://food.ec.europa.eu/food-safety/chemical-safety/food-contact-materials_en',
          },
        ],
        impact_level: 'medium',
        impact_summary:
          'Where similar polyurethane barrier materials are used for food-contact or '
          + 'process-contact use cases, suitability documentation and migration evidence may '
          + 'be requested by EU customers.',
        approval_state: 'in-force',
        enacted_date: '2004-10-27',
        effective_date: '2005-12-03',
        expected_enforcement_date: null,
        confidence: 0.73,
        recommended_action:
          'Validate whether this product family has any food-contact adjacency in customer '
          + 'or supplier requirements.',
      },
      {
        id: 'reg-watch-003',
        product_name: 'Adhesive Materials',
        product_unit_id: 47771,
        related_materials: ['Water-Resistant Backing Film'],
        factory_location: {
          label: 'ATTAIMAIRELAND / Dublin, Ireland',
          country: 'Ireland',
          region: 'Leinster',
        },
        regulation_name: 'Possible national consultation on fluorinated barrier coatings',
        regulatory_body: 'Unknown / not yet formalized',
        jurisdiction: 'France',
        source_type: 'media',
        source_links: [
          {
            label: 'Industry article',
            url: 'https://example.com/regulation-watch/fr-barrier-coatings',
          },
        ],
        impact_level: 'low',
        impact_summary:
          'Trade coverage suggests a possible consultation, but no formal regulator text '
          + 'has been confirmed.',
        approval_state: 'rumor',
        enacted_date: null,
        effective_date: null,
        expected_enforcement_date: null,
        confidence: 0.34,
        recommended_action: 'Keep on watchlist only; do not trigger operational response yet.',
      },
    ],
    summary: {
      total_results: 2,
      high_impact_count: 0,
      critical_impact_count: 0,
    },
  },
};

const FINANCE_DEMO_SIGNALS: Record<string, IFinanceMonitoringSignalResult> = {
  'run-fin-001-01': {
    findings: [
      {
        id: 'fin-run-1',
        supplier_name: 'Polymer Technologies',
        supplier_unit_id: 47426,
        related_materials: ['Polyethylene Film'],
        theme: 'payment-behaviour',
        title: 'Payment terms extension requested in recent filings',
        severity: 'high',
        confidence: 0.8,
        observed_at: '2026-07-16',
        source_type: 'filing',
        source_label: 'SEC filing',
        source_url: 'https://example.com/filings/polymer-pay-terms',
        shortage_risk: {
          level: 'elevated',
          summary: 'Early-pay / extended terms requests often precede delivery delays on feedstock.',
        },
        recommended_action: 'Review open PO exposure and confirm delivery schedules this week.',
      },
      {
        id: 'fin-run-2',
        supplier_name: 'Wacker Chemie AG',
        supplier_unit_id: 47428,
        related_materials: ['Silicone Gel'],
        theme: 'insolvency',
        title: 'Still operating under Chapter 11 bankruptcy protection',
        severity: 'critical',
        confidence: 0.95,
        observed_at: '2026-07-17',
        source_type: 'court',
        source_label: 'Court docket update',
        source_url: 'https://example.com/court/wacker-update',
        shortage_risk: {
          level: 'critical',
          summary: 'Ongoing protection status keeps silicone/adhesive allocation risk elevated.',
        },
        recommended_action: 'Keep Impact Scenario active; accelerate alternate qualification.',
      },
    ],
    entities: [
      {
        unitId: 47426,
        name: 'Polymer Technologies',
        stability_score: 29,
        trend: 'declining',
        spike: true,
        currency: 'USD',
        related_materials: ['Polyethylene Film', 'Adhesive Materials'],
        financial_profile: {
          years: [
            {
              year_index: 1, fiscal_year: 2025, revenue: 420000000, ebitda: 38000000, liquidity_retention: 0.41,
            },
            {
              year_index: 2, fiscal_year: 2024, revenue: 455000000, ebitda: 52000000, liquidity_retention: 0.48,
            },
            {
              year_index: 3, fiscal_year: 2023, revenue: 490000000, ebitda: 61000000, liquidity_retention: 0.55,
            },
            {
              year_index: 4, fiscal_year: 2022, revenue: 510000000, ebitda: 67000000, liquidity_retention: 0.58,
            },
            {
              year_index: 5, fiscal_year: 2021, revenue: 505000000, ebitda: 64000000, liquidity_retention: 0.57,
            },
          ],
        },
        bankruptcy_status: {
          status: 'none',
          label: 'No bankruptcy protection or judicial reorganization',
          is_active: false,
          history: [],
        },
        alerts: [
          {
            theme: 'Payment',
            title: 'Payment terms extension requested in filings',
            severity: 'high',
            source: 'SEC filing',
          },
          {
            theme: 'Liquidity',
            title: 'Liquidity retention (buffer) 0.41x — below 1.0 for 3 years',
            severity: 'high',
            source: 'Annual report',
          },
        ],
      },
      {
        unitId: 47427,
        name: 'Huntsman Corporation',
        stability_score: 68,
        trend: 'stable',
        spike: false,
        currency: 'USD',
        related_materials: ['Polyurethane Film', 'Silicone Gel'],
        financial_profile: {
          years: [
            {
              year_index: 1, fiscal_year: 2025, revenue: 6100000000, ebitda: 780000000, liquidity_retention: 1.12,
            },
            {
              year_index: 2, fiscal_year: 2024, revenue: 6050000000, ebitda: 760000000, liquidity_retention: 1.18,
            },
            {
              year_index: 3, fiscal_year: 2023, revenue: 5980000000, ebitda: 740000000, liquidity_retention: 1.21,
            },
            {
              year_index: 4, fiscal_year: 2022, revenue: 6200000000, ebitda: 810000000, liquidity_retention: 1.25,
            },
            {
              year_index: 5, fiscal_year: 2021, revenue: 5900000000, ebitda: 720000000, liquidity_retention: 1.15,
            },
          ],
        },
        bankruptcy_status: {
          status: 'none',
          label: 'No bankruptcy protection or judicial reorganization',
          is_active: false,
          history: [],
        },
        alerts: [
          {
            theme: 'Credit',
            title: 'Credit rating outlook revised to negative',
            severity: 'medium',
            source: 'S&P bulletin',
          },
        ],
      },
      {
        unitId: 47428,
        name: 'Wacker Chemie AG',
        stability_score: 12,
        trend: 'declining',
        spike: true,
        currency: 'USD',
        related_materials: ['Silicone Gel', 'Non-Woven Adhesive Material'],
        financial_profile: {
          years: [
            {
              year_index: 1, fiscal_year: 2025, revenue: 180000000, ebitda: -12000000, liquidity_retention: 0.18,
            },
            {
              year_index: 2, fiscal_year: 2024, revenue: 210000000, ebitda: 8000000, liquidity_retention: 0.29,
            },
            {
              year_index: 3, fiscal_year: 2023, revenue: 240000000, ebitda: 22000000, liquidity_retention: 0.36,
            },
            {
              year_index: 4, fiscal_year: 2022, revenue: 250000000, ebitda: 28000000, liquidity_retention: 0.40,
            },
            {
              year_index: 5, fiscal_year: 2021, revenue: 245000000, ebitda: 30000000, liquidity_retention: 0.42,
            },
          ],
        },
        bankruptcy_status: {
          status: 'chapter-11',
          label: 'Operating under Chapter 11 bankruptcy protection',
          jurisdiction: 'United States',
          case_name: 'In re Wacker Chemie AG (demo)',
          filed_at: '2026-03-12',
          is_active: true,
          history: [
            {
              status: 'chapter-11',
              filed_at: '2026-03-12',
              exited_at: null,
              note: 'Voluntary petition filed; debtor-in-possession operations continuing',
            },
          ],
        },
        alerts: [
          {
            theme: 'Insolvency',
            title: 'Chapter 11 — operating under bankruptcy protection',
            severity: 'critical',
            source: 'US Bankruptcy Court',
          },
          {
            theme: 'Liquidity',
            title: 'Liquidity retention (buffer) 0.18x',
            severity: 'critical',
            source: 'Annual report',
          },
        ],
      },
    ],
    summary: {
      total_findings: 2,
      high_risk_count: 2,
      entities_monitored: 3,
      entities_under_protection_count: 1,
      themes_frequency: { 'payment-behaviour': 1, insolvency: 1 },
    },
  },
};

const OWNERSHIP_DEMO_SIGNALS: Record<string, IOwnershipMonitoringSignalResult> = {
  'run-own-001-01': {
    summary: {
      entities_monitored: 3,
      total_findings: 5,
      high_risk_count: 4,
      entities_with_active_contagion_count: 2,
      themes_frequency: {
        'labour-rights': 1,
        'reputation-contagion': 2,
        environmental: 1,
        sanctions: 1,
      },
    },
    findings: [
      {
        id: 'own-001',
        supplier_name: 'Polymer Technologies',
        supplier_unit_id: 47426,
        related_materials: ['Polyethylene Film', 'Adhesive Materials'],
        theme: 'labour-rights',
        title: 'Child labour investigation at sister subsidiary linked to parent of Polymer Technologies',
        severity: 'critical',
        confidence: 0.91,
        observed_at: '2026-07-18',
        source_type: 'ngo-report',
        source_label: 'NGO field investigation',
        source_url: 'https://example.com/reports/northstar-labour-2026',
        link_path: [
          { entity_name: 'Polymer Technologies', role: 'Monitored supplier' },
          { entity_name: 'NorthStar Industrial Holdings', role: 'Direct parent' },
          { entity_name: 'NorthStar Apparel Manufacturing', role: 'Sister subsidiary' },
        ],
        contagion_mechanism:
          'OEM customers auditing entire NorthStar group after labour findings at sister subsidiary',
        shortage_risk: {
          level: 'critical',
          summary:
            'Group-wide stop-ship and audit requests may deprioritize polymer allocations to external customers '
            + 'including your film inputs.',
        },
        recommended_action:
          'Escalate to supplier management; request independent audit certificate; prepare dual-source '
          + 'for Polyethylene Film.',
      },
      {
        id: 'own-002',
        supplier_name: 'Polymer Technologies',
        supplier_unit_id: 47426,
        related_materials: ['Polyethylene Film'],
        theme: 'reputation-contagion',
        title: 'Chen Family Foundation named in bribery allegations in Southeast Asia media',
        severity: 'high',
        confidence: 0.74,
        observed_at: '2026-07-15',
        source_type: 'news',
        source_label: 'Regional business press',
        source_url: 'https://example.com/news/chen-foundation',
        link_path: [
          { entity_name: 'Polymer Technologies', role: 'Monitored supplier' },
          { entity_name: 'Chen Family Trust', role: 'Ultimate beneficial owner' },
          { entity_name: 'Chen Family Foundation', role: 'UBO foundation' },
        ],
        contagion_mechanism:
          'Reputational fallout from UBO-linked entity may trigger customer procurement reviews across group',
        shortage_risk: {
          level: 'elevated',
          summary: 'OEM procurement reviews of NorthStar group could slow PO renewals for polymer feedstock.',
        },
        recommended_action: 'Monitor sanctions watchlist updates for Chen Family Trust; confirm current PO coverage.',
      },
      {
        id: 'own-003',
        supplier_name: 'Polymer Technologies',
        supplier_unit_id: 47426,
        related_materials: ['Adhesive Materials'],
        theme: 'environmental',
        title: 'NorthStar Specialty Chemicals plant under regulator review after wastewater breach',
        severity: 'high',
        confidence: 0.82,
        observed_at: '2026-07-12',
        source_type: 'regulator',
        source_label: 'EU environmental gazette',
        source_url: 'https://example.com/regulator/northstar-wastewater',
        link_path: [
          { entity_name: 'Polymer Technologies', role: 'Monitored supplier' },
          { entity_name: 'NorthStar Industrial Holdings', role: 'Direct parent' },
          { entity_name: 'NorthStar Specialty Chemicals GmbH', role: 'Sister subsidiary' },
          { entity_name: 'NorthStar Specialty Chemicals — Plant A', role: 'Operating site' },
        ],
        contagion_mechanism:
          'Shared group permits and chemical park utilities mean a sister-plant review can constrain feedstock '
          + 'routing to Polymer Technologies',
        shortage_risk: {
          level: 'elevated',
          summary: 'Permit remediation at sister chemical plant may divert intermediate chemicals away from '
            + 'Polymer Technologies lines.',
        },
        recommended_action: 'Map shared intermediates; ask Polymer Technologies for contingency allocation plan.',
      },
      {
        id: 'own-004',
        supplier_name: 'Wacker Chemie AG',
        supplier_unit_id: 47428,
        related_materials: ['Silicone Gel'],
        theme: 'reputation-contagion',
        title: 'Ultimate beneficial owner named in cross-border corruption probe',
        severity: 'high',
        confidence: 0.78,
        observed_at: '2026-07-16',
        source_type: 'news',
        source_label: 'International business press',
        source_url: 'https://example.com/news/alpine-corruption',
        link_path: [
          { entity_name: 'Wacker Chemie AG', role: 'Monitored supplier' },
          { entity_name: 'Alpine Capital Partners', role: 'Ultimate beneficial owner' },
        ],
        contagion_mechanism: 'Banking and insurance review across Alpine portfolio companies',
        shortage_risk: {
          level: 'elevated',
          summary:
            'Payment and credit friction at group level can delay silicone feedstock shipments even if operating '
            + 'subsidiary remains open.',
        },
        recommended_action: 'Confirm payment terms and open PO coverage; monitor for sanctions watchlist updates.',
      },
      {
        id: 'own-005',
        supplier_name: 'Wacker Chemie AG',
        supplier_unit_id: 47428,
        related_materials: ['Silicone Gel'],
        theme: 'sanctions',
        title: 'Alpine Petrochemicals SpA added to enhanced due-diligence watchlist after trade routing alert',
        severity: 'high',
        confidence: 0.86,
        observed_at: '2026-07-17',
        source_type: 'watchlist',
        source_label: 'Trade compliance screening',
        source_url: 'https://example.com/watchlist/alpine-petro',
        link_path: [
          { entity_name: 'Wacker Chemie AG', role: 'Monitored supplier' },
          { entity_name: 'Alpine Capital Partners', role: 'Ultimate beneficial owner' },
          { entity_name: 'Alpine Petrochemicals SpA', role: 'Portfolio company' },
          { entity_name: 'Alpine Trade Desk LLC', role: 'Trading affiliate' },
        ],
        contagion_mechanism:
          'Shared treasury and trade-finance facilities across Alpine portfolio can freeze outbound shipments '
          + 'from Wacker Chemie AG',
        shortage_risk: {
          level: 'critical',
          summary: 'Trade-finance holds on Alpine affiliates may interrupt silicone gel deliveries into your '
            + 'Own Operations.',
        },
        recommended_action: 'Escalate compliance check; confirm alternate silicone sources for critical SKUs.',
      },
    ],
    entities: [
      {
        unitId: 47426,
        name: 'Polymer Technologies',
        contagion_score: 82,
        trend: 'rising',
        spike: true,
        related_materials: ['Polyethylene Film', 'Adhesive Materials'],
        ownership_profile: {
          ultimate_beneficial_owner: { name: 'Chen Family Trust', type: 'person-or-trust', jurisdiction: 'Singapore' },
          direct_parent: {
            name: 'NorthStar Industrial Holdings', type: 'company', ownership_stake_pct: 87, jurisdiction: 'Germany',
          },
          corporate_group: 'NorthStar Industrial Group',
          related_entities_scanned: 14,
          related_entities_flagged: 4,
          group_entities_preview: [
            {
              name: 'NorthStar Apparel Manufacturing',
              role: 'Sister subsidiary',
              jurisdiction: 'Vietnam',
              risk_flag: true,
            },
            {
              name: 'NorthStar Specialty Chemicals GmbH',
              role: 'Sister subsidiary',
              jurisdiction: 'Germany',
              risk_flag: true,
            },
            {
              name: 'Chen Family Foundation',
              role: 'UBO foundation',
              jurisdiction: 'Singapore',
              risk_flag: true,
            },
          ],
        },
        investigation_tree: [
          {
            id: 'poly-parent',
            name: 'NorthStar Industrial Holdings',
            role: 'Direct parent',
            jurisdiction: 'Germany',
            ownership_stake_pct: 87,
            risk_flag: true,
            children: [
              {
                id: 'poly-sister-apparel',
                name: 'NorthStar Apparel Manufacturing',
                role: 'Sister subsidiary',
                jurisdiction: 'Vietnam',
                risk_flag: true,
                finding_ids: ['own-001'],
              },
              {
                id: 'poly-sister-chem',
                name: 'NorthStar Specialty Chemicals GmbH',
                role: 'Sister subsidiary',
                jurisdiction: 'Germany',
                risk_flag: true,
                children: [
                  {
                    id: 'poly-plant-a',
                    name: 'NorthStar Specialty Chemicals — Plant A',
                    role: 'Operating site',
                    jurisdiction: 'Germany',
                    risk_flag: true,
                    finding_ids: ['own-003'],
                  },
                ],
              },
              {
                id: 'poly-logistics',
                name: 'NorthStar Logistics GmbH',
                role: 'Sister subsidiary',
                jurisdiction: 'Germany',
                risk_flag: false,
                finding_ids: [],
              },
            ],
          },
          {
            id: 'poly-ubo',
            name: 'Chen Family Trust',
            role: 'Ultimate beneficial owner',
            jurisdiction: 'Singapore',
            risk_flag: true,
            children: [
              {
                id: 'poly-foundation',
                name: 'Chen Family Foundation',
                role: 'UBO foundation',
                jurisdiction: 'Singapore',
                risk_flag: true,
                finding_ids: ['own-002'],
              },
            ],
          },
        ],
        alerts: [
          {
            theme: 'Labour',
            title: 'Child labour investigation at NorthStar Apparel Manufacturing (sister subsidiary)',
            severity: 'critical',
            source: 'NGO field investigation',
          },
          {
            theme: 'Environmental',
            title: 'Sister chemical plant under regulator review',
            severity: 'high',
            source: 'EU environmental gazette',
          },
          {
            theme: 'Reputation',
            title: 'UBO foundation named in bribery allegations',
            severity: 'high',
            source: 'Regional business press',
          },
        ],
      },
      {
        unitId: 47428,
        name: 'Wacker Chemie AG',
        contagion_score: 61,
        trend: 'rising',
        spike: true,
        related_materials: ['Silicone Gel'],
        ownership_profile: {
          ultimate_beneficial_owner: { name: 'Alpine Capital Partners', type: 'company', jurisdiction: 'Switzerland' },
          direct_parent: {
            name: 'Alpine Capital Partners', type: 'company', ownership_stake_pct: 51, jurisdiction: 'Switzerland',
          },
          corporate_group: 'Alpine Capital portfolio',
          related_entities_scanned: 22,
          related_entities_flagged: 3,
          group_entities_preview: [
            {
              name: 'Alpine Petrochemicals SpA', role: 'Portfolio company', jurisdiction: 'Italy', risk_flag: true,
            },
            {
              name: 'Alpine Trade Desk LLC', role: 'Trading affiliate', jurisdiction: 'United States', risk_flag: true,
            },
            {
              name: 'Alpine Logistics AG', role: 'Portfolio company', jurisdiction: 'Switzerland', risk_flag: false,
            },
          ],
        },
        investigation_tree: [
          {
            id: 'wacker-ubo',
            name: 'Alpine Capital Partners',
            role: 'Ultimate beneficial owner / Direct parent',
            jurisdiction: 'Switzerland',
            ownership_stake_pct: 51,
            risk_flag: true,
            finding_ids: ['own-004'],
            children: [
              {
                id: 'wacker-petro',
                name: 'Alpine Petrochemicals SpA',
                role: 'Portfolio company',
                jurisdiction: 'Italy',
                risk_flag: true,
                children: [
                  {
                    id: 'wacker-trade',
                    name: 'Alpine Trade Desk LLC',
                    role: 'Trading affiliate',
                    jurisdiction: 'United States',
                    risk_flag: true,
                    finding_ids: ['own-005'],
                  },
                ],
              },
              {
                id: 'wacker-logistics',
                name: 'Alpine Logistics AG',
                role: 'Portfolio company',
                jurisdiction: 'Switzerland',
                risk_flag: false,
                finding_ids: [],
              },
            ],
          },
        ],
        alerts: [
          {
            theme: 'Reputation',
            title: 'UBO named in cross-border corruption probe',
            severity: 'high',
            source: 'International business press',
          },
          {
            theme: 'Sanctions',
            title: 'Portfolio company on enhanced due-diligence watchlist',
            severity: 'high',
            source: 'Trade compliance screening',
          },
        ],
      },
      {
        unitId: 47427,
        name: 'Huntsman Corporation',
        contagion_score: 18,
        trend: 'stable',
        spike: false,
        related_materials: ['Polyurethane Film'],
        ownership_profile: {
          ultimate_beneficial_owner: {
            name: 'Huntsman Family', type: 'person-or-trust', jurisdiction: 'United States',
          },
          direct_parent: {
            name: 'Huntsman Holdings LLC', type: 'company', ownership_stake_pct: 100, jurisdiction: 'United States',
          },
          corporate_group: 'Huntsman Corporation',
          related_entities_scanned: 31,
          related_entities_flagged: 0,
          group_entities_preview: [],
        },
        investigation_tree: [
          {
            id: 'huntsman-parent',
            name: 'Huntsman Holdings LLC',
            role: 'Direct parent',
            jurisdiction: 'United States',
            ownership_stake_pct: 100,
            risk_flag: false,
            finding_ids: [],
            children: [
              {
                id: 'huntsman-ubo',
                name: 'Huntsman Family',
                role: 'Ultimate beneficial owner',
                jurisdiction: 'United States',
                risk_flag: false,
                finding_ids: [],
              },
            ],
          },
        ],
        alerts: [],
      },
    ],
  },
  'run-own-001-02': {
    summary: {
      entities_monitored: 3,
      total_findings: 2,
      high_risk_count: 1,
      entities_with_active_contagion_count: 1,
      themes_frequency: {
        governance: 1,
        environmental: 1,
      },
    },
    findings: [
      {
        id: 'own-run2-001',
        supplier_name: 'Polymer Technologies',
        supplier_unit_id: 47426,
        related_materials: ['Polyethylene Film'],
        theme: 'governance',
        title: 'NorthStar ownership restructuring filed with German commercial registry',
        severity: 'medium',
        confidence: 0.7,
        observed_at: '2026-07-13',
        source_type: 'registry',
        source_label: 'Commercial registry',
        link_path: [
          { entity_name: 'Polymer Technologies', role: 'Monitored supplier' },
          { entity_name: 'NorthStar Industrial Holdings', role: 'Direct parent' },
        ],
        contagion_mechanism: 'Control changes at parent can alter capital allocation and customer priority rules',
        shortage_risk: {
          level: 'watch',
          summary: 'No immediate stop-ship, but ownership change warrants elevated monitoring cadence.',
        },
        recommended_action: 'Request updated ownership attestation from Polymer Technologies.',
      },
      {
        id: 'own-run2-002',
        supplier_name: 'Wacker Chemie AG',
        supplier_unit_id: 47428,
        related_materials: ['Silicone Gel'],
        theme: 'environmental',
        title: 'Alpine Petrochemicals SpA emissions review initiated',
        severity: 'high',
        confidence: 0.8,
        observed_at: '2026-07-12',
        source_type: 'regulator',
        source_label: 'EU regulatory gazette',
        link_path: [
          { entity_name: 'Wacker Chemie AG', role: 'Monitored supplier' },
          { entity_name: 'Alpine Capital Partners', role: 'Ultimate beneficial owner' },
          { entity_name: 'Alpine Petrochemicals SpA', role: 'Portfolio company' },
        ],
        contagion_mechanism:
          'Portfolio environmental enforcement can tighten group insurance and operating constraints',
        shortage_risk: {
          level: 'elevated',
          summary: 'Group remediation spend may reduce silicone specialty allocation flexibility.',
        },
        recommended_action: 'Confirm Wacker Chemie AG inventory buffer for Silicone Gel.',
      },
    ],
    entities: [
      {
        unitId: 47426,
        name: 'Polymer Technologies',
        contagion_score: 74,
        trend: 'rising',
        spike: false,
        ownership_profile: {
          ultimate_beneficial_owner: { name: 'Chen Family Trust', type: 'person-or-trust', jurisdiction: 'Singapore' },
          direct_parent: {
            name: 'NorthStar Industrial Holdings', type: 'company', ownership_stake_pct: 87, jurisdiction: 'Germany',
          },
          corporate_group: 'NorthStar Industrial Group',
          related_entities_scanned: 14,
          related_entities_flagged: 2,
          group_entities_preview: [],
        },
        investigation_tree: [
          {
            id: 'run2-poly-parent',
            name: 'NorthStar Industrial Holdings',
            role: 'Direct parent',
            jurisdiction: 'Germany',
            risk_flag: true,
            finding_ids: ['own-run2-001'],
          },
        ],
        alerts: [
          {
            theme: 'Governance',
            title: 'NorthStar ownership restructuring filed with registry',
            severity: 'medium',
            source: 'Commercial registry',
          },
        ],
      },
      {
        unitId: 47428,
        name: 'Wacker Chemie AG',
        contagion_score: 55,
        trend: 'stable',
        spike: false,
        ownership_profile: {
          ultimate_beneficial_owner: { name: 'Alpine Capital Partners', type: 'company', jurisdiction: 'Switzerland' },
          direct_parent: {
            name: 'Alpine Capital Partners', type: 'company', ownership_stake_pct: 51, jurisdiction: 'Switzerland',
          },
          corporate_group: 'Alpine Capital portfolio',
          related_entities_scanned: 22,
          related_entities_flagged: 1,
          group_entities_preview: [],
        },
        investigation_tree: [
          {
            id: 'run2-wacker-ubo',
            name: 'Alpine Capital Partners',
            role: 'Ultimate beneficial owner',
            jurisdiction: 'Switzerland',
            risk_flag: true,
            children: [
              {
                id: 'run2-wacker-petro',
                name: 'Alpine Petrochemicals SpA',
                role: 'Portfolio company',
                jurisdiction: 'Italy',
                risk_flag: true,
                finding_ids: ['own-run2-002'],
              },
            ],
          },
        ],
        alerts: [
          {
            theme: 'Environmental',
            title: 'Alpine Petrochemicals SpA emissions review initiated',
            severity: 'high',
            source: 'EU regulatory gazette',
          },
        ],
      },
      {
        unitId: 47427,
        name: 'Huntsman Corporation',
        contagion_score: 12,
        trend: 'stable',
        spike: false,
        ownership_profile: {
          ultimate_beneficial_owner: {
            name: 'Huntsman Family', type: 'person-or-trust', jurisdiction: 'United States',
          },
          direct_parent: {
            name: 'Huntsman Holdings LLC', type: 'company', ownership_stake_pct: 100, jurisdiction: 'United States',
          },
          corporate_group: 'Huntsman Corporation',
          related_entities_scanned: 31,
          related_entities_flagged: 0,
          group_entities_preview: [],
        },
        investigation_tree: [],
        alerts: [],
      },
    ],
  },
};

export const MONITORING_AGENT_DEMO_SIGNALS: Record<string, TMonitoringAnySignalResult> = {
  ...REPUTATION_DEMO_SIGNALS,
  ...REGULATION_DEMO_SIGNALS,
  ...FINANCE_DEMO_SIGNALS,
  ...OWNERSHIP_DEMO_SIGNALS,
  ...CLIMATE_DEMO_SIGNALS,
};
